// The deck engine's API surface (routes/decks.ts) — decks, their slides, the live-resolved
// bound data, the export trail, and the two thin libraries (people, mandate references) the
// editor's pickers read. Pattern: api/proposals.ts — request<T> only, never raw fetch.
//
// The response shapes mirror the brief's route contract:
//   GET  /decks?engagement=        → Deck[]
//   POST /decks                    → Deck (standard slides created server-side)
//   GET  /decks/:id                → { deck, slides }
//   PATCH /decks/:id               → Deck
//   PATCH /decks/:id/slides/:sid   → DeckSlide  (400 {error, violations} on budget overflow)
//   GET  /decks/:id/bound          → { [slideId]: Partial<DeckBoundByKind> } — one fetch for
//                                    every bound slide of the deck; slides without bound data
//                                    simply have no key.
//   POST /decks/:id/export         → DeckVersion
//   GET  /decks/:id/versions       → DeckVersion[]
//   GET  /deck-versions/:id/download  (file — linked, not requested as JSON)
import { request, upload } from './client.js';
import type { DeckBoundByKind, DeckSlideType, SlotViolation } from '../../shared/deckTypes.js';

export interface Deck {
  id: number;
  engagement_id: number;
  title: string;
  language: string;
  status: string;
  created_by: number | null;
  created_at: string;
  updated_at: string;
  /** List rows only: how many slides are toggled on. */
  included_slides?: number;
  /** List rows only: the newest export's version_no, null before the first export. */
  latest_version_no?: number | null;
}

export interface DeckSlide {
  id: number;
  deck_id: number;
  slide_type: DeckSlideType;
  position: number;
  included: boolean;
  content: Record<string, unknown>;
  updated_at: string;
}

export interface DeckDetail {
  deck: Deck;
  slides: DeckSlide[];
}

/** What one slide's live register data looks like — at most one kind per slide type today. */
export type SlideBound = Partial<DeckBoundByKind>;
/** Keyed by deck_slides.id (JSON object keys are strings). */
export type DeckBoundMap = Record<string, SlideBound>;

export interface DeckVersion {
  id: number;
  deck_id: number;
  version_no: number;
  content_hash: string;
  pptx_key: string | null;
  created_by: number | null;
  created_at: string;
}

/** The 400 body a slide PATCH answers with when a slot breaks its budget. */
export interface SlideViolationBody {
  error: string;
  violations: SlotViolation[];
}

export interface DeckImage {
  /** Slide-JSON key AND preview src: '/deck-images/<file>'. */
  key: string;
  url: string;
  bytes: number;
  uploaded_at: string;
}

export interface DeckImageLibrary {
  uploaded: DeckImage[];
  /** Shipped or uploaded keys the operator took out of the picker. */
  hidden: string[];
}

export interface DeckPersonProfile {
  context_key: string;
  role_label: string;
  bio: string;
}

export interface DeckPerson {
  id: number;
  name: string;
  photo_key: string | null;
  sort_order: number;
  /** The person's per-context profiles, inline from GET /api/people. */
  profiles: DeckPersonProfile[];
}

export interface MandateReference {
  id: number;
  slug: string;
  context_label: string;
  claim: string;
  body: string;
  takeaway: string;
  mood_image_key: string | null;
  logo_key: string | null;
  logo_redacted: boolean;
  sort_order: number;
}

export const decksApi = {
  list: (engagementId: number) => request<Deck[]>(`/decks?engagement=${engagementId}`),

  create: (engagementId: number, title: string) =>
    request<Deck>('/decks', {
      method: 'POST',
      body: JSON.stringify({ engagement_id: engagementId, title }),
    }),

  get: (id: number) => request<DeckDetail>(`/decks/${id}`),

  update: (id: number, patch: Partial<Pick<Deck, 'title' | 'language' | 'status'>>) =>
    request<Deck>(`/decks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  updateSlide: (
    deckId: number,
    slideId: number,
    patch: { content?: Record<string, unknown>; included?: boolean; position?: number },
  ) =>
    request<DeckSlide>(`/decks/${deckId}/slides/${slideId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  bound: (id: number) => request<DeckBoundMap>(`/decks/${id}/bound`),

  export: (id: number) => request<DeckVersion>(`/decks/${id}/export`, { method: 'POST' }),

  versions: (id: number) => request<DeckVersion[]>(`/decks/${id}/versions`),

  /** The download is a file stream — used as an <a href>, never through request<T>. */
  versionDownloadUrl: (versionId: number) => `/api/deck-versions/${versionId}/download`,

  addSlide: (deckId: number, slideType: string, afterSlideId: number | null) =>
    request<DeckSlide>(`/decks/${deckId}/slides`, {
      method: 'POST',
      body: JSON.stringify({ slide_type: slideType, after_slide_id: afterSlideId }),
    }),

  removeSlide: (deckId: number, slideId: number) =>
    request<{ ok: boolean }>(`/decks/${deckId}/slides/${slideId}`, { method: 'DELETE' }),

  /** The operator's uploaded imagery (beside the shipped /deck-assets set) + hidden keys. */
  images: () => request<DeckImageLibrary>('/deck-images'),

  hideImage: (key: string) =>
    request<{ ok: boolean }>('/deck-images/hide', { method: 'POST', body: JSON.stringify({ key }) }),

  unhideImage: (key: string) =>
    request<{ ok: boolean }>('/deck-images/unhide', { method: 'POST', body: JSON.stringify({ key }) }),

  uploadImage: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return upload<DeckImage>('/deck-images', form);
  },

  deleteImage: (key: string) =>
    request<{ ok: boolean }>(`/deck-images/${encodeURIComponent(key.replace(/^\/deck-images\//, ''))}`, { method: 'DELETE' }),

  people: () => request<DeckPerson[]>('/people'),

  mandateReferences: () => request<MandateReference[]>('/mandate-references'),
};

export default decksApi;
